'use client'

import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  Rocket01Icon,
  Search01Icon,
  Upload01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  JOURNEY_CHANNEL_LABEL,
  type Customer,
  type JourneyChannel,
} from '@/lib/customers/customer-row'

type LookalikeAudienceModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  customers: Customer[]
  onCreated: (id: string) => void
}

type SeedSource = 'current' | 'csv'
type SortKey = 'recent' | 'spend_desc' | 'orders_desc'
type IdentifierKey = 'email_sha256' | 'phone_sha256' | 'user_id'
type ActivationChannelKey = 'tv' | 'programmatic' | 'social' | 'search' | 'retail'

type ActivationChannel = {
  key: ActivationChannelKey
  label: string
  subchannels: Array<{ key: string; label: string }>
}

const IDENTIFIERS: Array<{ key: IdentifierKey; label: string; recommended?: boolean }> = [
  { key: 'email_sha256', label: 'email_sha256', recommended: true },
  { key: 'phone_sha256', label: 'phone_sha256' },
  { key: 'user_id', label: 'user_id' },
]

const SEED_CHANNELS: JourneyChannel[] = ['search', 'social', 'tv', 'email', 'direct']

const ACTIVATION_CHANNELS: ActivationChannel[] = [
  { key: 'tv', label: 'TV', subchannels: [{ key: 'roku', label: 'Roku' }, { key: 'universal_ads', label: 'Universal Ads' }, { key: 'sonobi', label: 'Atrium Performance Network' }] },
  { key: 'programmatic', label: 'Programmatic', subchannels: [{ key: 'the_trade_desk', label: 'The Trade Desk' }] },
  { key: 'social', label: 'Social', subchannels: [{ key: 'meta', label: 'Meta' }] },
  { key: 'search', label: 'Search', subchannels: [{ key: 'google_ads', label: 'Google Ads' }] },
  { key: 'retail', label: 'Retail', subchannels: [{ key: 'amazon', label: 'Amazon' }] },
]

export function LookalikeAudienceModal({
  open,
  onOpenChange,
  customers,
  onCreated,
}: LookalikeAudienceModalProps) {
  const router = useRouter()
  const fileRef = useRef<HTMLInputElement>(null)
  const [seedSource, setSeedSource] = useState<SeedSource>('current')
  const [hasEmail, setHasEmail] = useState(false)
  const [hasPhone, setHasPhone] = useState(false)
  const [hasUserId, setHasUserId] = useState(false)
  const [seedChannels, setSeedChannels] = useState<JourneyChannel[]>([])
  const [minSpend, setMinSpend] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('spend_desc')
  const [selectAllMatching, setSelectAllMatching] = useState(true)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvRows, setCsvRows] = useState(0)
  const [csvBatchId, setCsvBatchId] = useState<string | null>(null)
  const [typedRows, setTypedRows] = useState('')
  const [identifiers, setIdentifiers] = useState<IdentifierKey[]>(['email_sha256'])
  const [activationChannels, setActivationChannels] = useState<ActivationChannelKey[]>([])
  const [subchannels, setSubchannels] = useState<Record<ActivationChannelKey, { key: string | null; label: string }>>({} as Record<ActivationChannelKey, { key: string | null; label: string }>)
  const [name, setName] = useState('')
  const [subchannelOpen, setSubchannelOpen] = useState<ActivationChannelKey | null>(null)
  const [customSubchannel, setCustomSubchannel] = useState<Record<ActivationChannelKey, string>>({} as Record<ActivationChannelKey, string>)
  const [creating, setCreating] = useState(false)

  const minSpendCents = Math.max(0, Math.round((Number(minSpend) || 0) * 100))
  const matchingCustomers = useMemo(() => {
    const filtered = customers.filter((customer) => {
      if (hasEmail && !customer.email) return false
      if (hasPhone && !customer.phone) return false
      if (hasUserId && !customer.id) return false
      if (seedChannels.length > 0 && !seedChannels.some((channel) => customer.channels.includes(channel))) return false
      if (minSpendCents > 0 && customer.totalSpentCents < minSpendCents) return false
      return true
    })

    return [...filtered].sort((a, b) => {
      if (sortKey === 'recent') return a.firstSeenDaysAgo - b.firstSeenDaysAgo
      if (sortKey === 'orders_desc') return b.ordersCount - a.ordersCount
      return b.totalSpentCents - a.totalSpentCents
    })
  }, [customers, hasEmail, hasPhone, hasUserId, minSpendCents, seedChannels, sortKey])

  const seedCount = selectAllMatching ? matchingCustomers.length : 0
  const pastedRowCount = typedRows.split(/\r?\n/).map((row) => row.trim()).filter(Boolean).length
  const canCreate = name.trim().length > 0 && identifiers.length > 0 && activationChannels.length > 0 && !creating

  function reset() {
    setSeedSource('current')
    setHasEmail(false)
    setHasPhone(false)
    setHasUserId(false)
    setSeedChannels([])
    setMinSpend('')
    setSortKey('spend_desc')
    setSelectAllMatching(true)
    setCsvFile(null)
    setCsvRows(0)
    setCsvBatchId(null)
    setTypedRows('')
    setIdentifiers(['email_sha256'])
    setActivationChannels([])
    setSubchannels({} as Record<ActivationChannelKey, { key: string | null; label: string }>)
    setName('')
    setCustomSubchannel({} as Record<ActivationChannelKey, string>)
    setCreating(false)
  }

  function close() {
    onOpenChange(false)
    reset()
  }

  async function handleCsvUpload(file: File | null) {
    if (!file) return
    setCsvFile(file)
    toast.loading('Uploading seed CSV...', { id: 'lookalike-csv' })

    const fd = new FormData()
    fd.append('file', file)
    const res = await fetch('/api/customers/upload', { method: 'POST', body: fd })
    const payload = await res.json().catch(() => null) as {
      batch_id?: string
      total_rows?: number
      inserted?: number
      updated?: number
      error?: string
    } | null

    if (!res.ok || !payload?.batch_id) {
      setCsvFile(null)
      setCsvRows(0)
      setCsvBatchId(null)
      toast.error(payload?.error ?? 'Could not upload seed CSV.', { id: 'lookalike-csv' })
      return
    }

    setCsvBatchId(payload.batch_id)
    setCsvRows(payload.total_rows ?? 0)
    toast.success(`${payload.inserted ?? 0} new + ${payload.updated ?? 0} updated`, { id: 'lookalike-csv' })
  }

  function toggleIdentifier(identifier: IdentifierKey) {
    setIdentifiers((prev) => (
      prev.includes(identifier) ? prev.filter((item) => item !== identifier) : [...prev, identifier]
    ))
  }

  function toggleActivationChannel(channel: ActivationChannelKey) {
    setActivationChannels((prev) => (
      prev.includes(channel) ? prev.filter((item) => item !== channel) : [...prev, channel]
    ))
  }

  async function onCreate() {
    if (!canCreate) return
    setCreating(true)
    const payload = {
      kind: 'lookalike',
      name: name.trim(),
      seed: seedSource === 'csv'
        ? {
            source: 'csv',
            filename: csvFile?.name ?? null,
            received_rows: csvRows + pastedRowCount,
            customer_source_batch_id: csvBatchId,
          }
        : {
            source: 'current',
            filters: { hasEmail, hasPhone, hasUserId, channels: seedChannels, minSpendCents },
            estimated_seed_count: seedCount,
          },
      identifiers,
      channels: activationChannels.map((channel) => ({
        channel,
        subchannel: subchannels[channel]?.key ?? subchannels[channel]?.label ?? null,
      })),
    }

    const res = await fetch('/api/customers/audiences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!res.ok) {
      setCreating(false)
      toast.error('Could not create lookalike. Try again.')
      return
    }

    const { id } = (await res.json()) as { id: string }
    toast.success(`Lookalike "${name.trim()}" created.`)
    onCreated(id)
    onOpenChange(false)
    reset()
    router.push(`/customers/audiences/${id}`)
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : close())}>
      <DialogContent showCloseButton={false} className="max-h-[88vh] overflow-hidden rounded-2xl border-0 bg-white p-0 dark:bg-surface sm:max-w-[720px]">
        <div className="relative bg-[linear-gradient(135deg,#006241,#2BC39A)] px-6 pb-5 pt-6 text-white">
          <DialogTitle className="text-[18px] font-extrabold tracking-[-0.01em] text-white">
            Lookalike audiences
          </DialogTitle>
          <p className="mt-1 text-[12.5px] text-white/85">
            Seed from first-party customers and activate across built channels.
          </p>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="absolute right-4 top-4 grid size-7 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25"
          >
            <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
          </button>
        </div>

        <div className="max-h-[calc(88vh-144px)] overflow-y-auto px-5 py-4">
          <Section title="Seed customers">
            <div className="grid gap-2 sm:grid-cols-2">
              <RadioCard
                selected={seedSource === 'current'}
                title="Use current customers"
                body="Filter this customer list and select all matches."
                onClick={() => setSeedSource('current')}
              />
              <RadioCard
                selected={seedSource === 'csv'}
                title="Upload CSV"
                body="Accept a file or typed list for matching on submit."
                onClick={() => setSeedSource('csv')}
              />
            </div>

            {seedSource === 'current' ? (
              <div className="mt-3 rounded-xl border border-black/10 bg-[#FAFAF7] p-3 dark:border-white/10 dark:bg-atrium-inner">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip active={hasEmail} onClick={() => setHasEmail((value) => !value)}>Has email</Chip>
                  <Chip active={hasPhone} onClick={() => setHasPhone((value) => !value)}>Has phone</Chip>
                  <Chip active={hasUserId} onClick={() => setHasUserId((value) => !value)}>Has user_id</Chip>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {SEED_CHANNELS.map((channel) => (
                    <Chip
                      key={channel}
                      active={seedChannels.includes(channel)}
                      onClick={() => setSeedChannels((prev) => prev.includes(channel) ? prev.filter((item) => item !== channel) : [...prev, channel])}
                    >
                      {JOURNEY_CHANNEL_LABEL[channel]}
                    </Chip>
                  ))}
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_180px]">
                  <label className="block">
                    <span className="text-[12px] font-semibold text-text-strong">Min spend</span>
                    <input
                      type="number"
                      min="0"
                      value={minSpend}
                      onChange={(event) => setMinSpend(event.target.value)}
                      placeholder="0"
                      className="mt-1.5 h-10 w-full rounded-xl border-[1.5px] border-black/10 bg-white px-3 text-sm text-text-strong outline-none focus:border-[#0A8A6E] dark:border-white/10 dark:bg-surface"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[12px] font-semibold text-text-strong">Sort</span>
                    <select
                      value={sortKey}
                      onChange={(event) => setSortKey(event.target.value as SortKey)}
                      className="mt-1.5 h-10 w-full rounded-xl border-[1.5px] border-black/10 bg-white px-3 text-sm text-text-strong outline-none focus:border-[#0A8A6E] dark:border-white/10 dark:bg-surface"
                    >
                      <option value="spend_desc">Spend high to low</option>
                      <option value="orders_desc">Orders high to low</option>
                      <option value="recent">Most recent</option>
                    </select>
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectAllMatching((value) => !value)}
                    className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-text-strong hover:bg-black/[0.03] dark:border-white/10 dark:bg-surface dark:hover:bg-white/[0.05]"
                  >
                    {selectAllMatching && <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-4 text-[#0A8A6E]" />}
                    Select all matching
                  </button>
                  <span className="font-mono text-[12px] text-text">
                    Seed size: ~{seedCount.toLocaleString()} customers
                  </span>
                </div>
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-black/10 bg-[#FAFAF7] p-3 dark:border-white/10 dark:bg-atrium-inner">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.tsv,.txt"
                  className="hidden"
                  onChange={(event) => void handleCsvUpload(event.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-[11px] border border-black/10 bg-white px-3 py-2 text-[13px] font-bold text-text-strong hover:bg-black/[0.03] dark:border-white/10 dark:bg-surface dark:hover:bg-white/[0.05]"
                >
                  <HugeiconsIcon icon={Upload01Icon} className="size-4 text-[#0A8A6E]" />
                  Choose file
                </button>
                <textarea
                  value={typedRows}
                  onChange={(event) => setTypedRows(event.target.value)}
                  rows={3}
                  placeholder="Or paste one customer identifier per line"
                  className="mt-3 w-full resize-none rounded-xl border-[1.5px] border-black/10 bg-white px-3 py-2.5 text-sm leading-[1.5] text-text-strong outline-none placeholder:text-[#b6b4ac] focus:border-[#0A8A6E] dark:border-white/10 dark:bg-surface dark:placeholder:text-atrium-muted"
                />
                {(csvRows > 0 || pastedRowCount > 0) && (
                  <p className="mt-2 text-[12.5px] text-text">
                    Received {(csvRows + pastedRowCount).toLocaleString()} rows. We&apos;ll match against first-party data when you submit.
                  </p>
                )}
              </div>
            )}
          </Section>

          <Section title="Match on">
            <div className="flex flex-wrap gap-2">
              {IDENTIFIERS.map((identifier) => (
                <Chip
                  key={identifier.key}
                  active={identifiers.includes(identifier.key)}
                  onClick={() => toggleIdentifier(identifier.key)}
                >
                  {identifier.label}{identifier.recommended ? ' (recommended)' : ''}
                </Chip>
              ))}
            </div>
          </Section>

          <Section title="Activate across">
            <div className="flex flex-wrap gap-2">
              {ACTIVATION_CHANNELS.map((channel) => (
                <Chip
                  key={channel.key}
                  active={activationChannels.includes(channel.key)}
                  onClick={() => toggleActivationChannel(channel.key)}
                >
                  {channel.label}
                </Chip>
              ))}
            </div>
            {activationChannels.length > 0 && (
              <div className="mt-3 space-y-2">
                {ACTIVATION_CHANNELS.filter((channel) => activationChannels.includes(channel.key)).map((channel) => (
                  <SubchannelPicker
                    key={channel.key}
                    channel={channel}
                    selected={subchannels[channel.key] ?? null}
                    open={subchannelOpen === channel.key}
                    customValue={customSubchannel[channel.key] ?? ''}
                    onOpenChange={(nextOpen) => setSubchannelOpen(nextOpen ? channel.key : null)}
                    onCustomChange={(value) => setCustomSubchannel((prev) => ({ ...prev, [channel.key]: value }))}
                    onSelect={(value) => {
                      setSubchannels((prev) => ({ ...prev, [channel.key]: value }))
                      setCustomSubchannel((prev) => ({ ...prev, [channel.key]: '' }))
                      setSubchannelOpen(null)
                    }}
                  />
                ))}
              </div>
            )}
          </Section>

          <Section title="Name your lookalike">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. High-value HVAC buyers lookalike"
              className="h-11 w-full rounded-xl border-[1.5px] border-black/10 bg-[#FAFAF7] px-3 text-sm text-text-strong outline-none placeholder:text-[#b6b4ac] focus:border-[#0A8A6E] dark:border-white/10 dark:bg-atrium-inner dark:placeholder:text-atrium-muted"
            />
          </Section>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-black/[0.06] px-5 py-4 dark:border-white/[0.08]">
          <button
            type="button"
            onClick={close}
            className="rounded-[11px] border border-black/10 bg-white px-[15px] py-2.5 text-[13px] font-bold text-text-strong hover:bg-black/[0.03] dark:border-white/10 dark:bg-atrium-inner dark:hover:bg-white/[0.05]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canCreate}
            onClick={() => void onCreate()}
            className="inline-flex items-center gap-1.5 rounded-[11px] bg-[#0A8A6E] px-[15px] py-2.5 text-[13px] font-bold text-white hover:bg-[#0a7a62] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <HugeiconsIcon icon={Rocket01Icon} className="size-4" />
            {creating ? 'Creating...' : 'Create lookalike'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-black/[0.06] py-4 last:border-b-0 dark:border-white/[0.08]">
      <h2 className="mb-2 text-[13px] font-extrabold text-text-strong">{title}</h2>
      {children}
    </section>
  )
}

function RadioCard({ selected, title, body, onClick }: { selected: boolean; title: string; body: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border p-3 text-left transition-colors ${
        selected
          ? 'border-[#0A8A6E] bg-[#0A8A6E]/[0.06] dark:border-[#2BC39A] dark:bg-[#2BC39A]/[0.08]'
          : 'border-black/10 bg-white hover:bg-black/[0.02] dark:border-white/10 dark:bg-atrium-inner dark:hover:bg-white/[0.04]'
      }`}
    >
      <span className="flex items-center gap-2 text-[13px] font-bold text-text-strong">
        <span className={`grid size-4 place-items-center rounded-full border ${selected ? 'border-[#0A8A6E] dark:border-[#2BC39A]' : 'border-black/20 dark:border-white/20'}`}>
          {selected && <span className="size-2 rounded-full bg-[#0A8A6E]" />}
        </span>
        {title}
      </span>
      <span className="mt-1 block text-[12.5px] leading-5 text-text">{body}</span>
    </button>
  )
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
        active
          ? 'border-[#0A8A6E] bg-[#0A8A6E] text-white dark:border-[#2BC39A] dark:bg-[#2BC39A]'
          : 'border-black/10 bg-white text-text-strong hover:bg-black/[0.03] dark:border-white/10 dark:bg-surface dark:hover:bg-white/[0.05]'
      }`}
    >
      {active && <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-3.5" />}
      {children}
    </button>
  )
}

function SubchannelPicker({
  channel,
  selected,
  open,
  customValue,
  onOpenChange,
  onCustomChange,
  onSelect,
}: {
  channel: ActivationChannel
  selected: { key: string | null; label: string } | null
  open: boolean
  customValue: string
  onOpenChange: (open: boolean) => void
  onCustomChange: (value: string) => void
  onSelect: (value: { key: string | null; label: string }) => void
}) {
  const trimmedCustom = customValue.trim()
  return (
    <div className="rounded-xl border border-black/10 bg-[#FAFAF7] p-3 dark:border-white/10 dark:bg-atrium-inner">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-text-strong">{channel.label} subchannel</span>
        {selected?.label && (
          <button
            type="button"
            onClick={() => onSelect({ key: null, label: '' })}
            className="text-[12px] font-semibold text-[#939294] hover:text-text-strong"
          >
            Clear
          </button>
        )}
      </div>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 w-full items-center justify-between rounded-xl border-[1.5px] border-black/10 bg-white px-3 text-left text-sm text-text-strong outline-none hover:bg-black/[0.02] dark:border-white/10 dark:bg-surface dark:hover:bg-white/[0.04]"
          >
            <span className="flex min-w-0 items-center gap-2 truncate">
              <HugeiconsIcon icon={Search01Icon} className="size-4 shrink-0 text-[#939294]" />
              {selected?.label ? selected.label : 'Subchannel (optional)'}
            </span>
            <HugeiconsIcon icon={ArrowDown01Icon} className="size-4 shrink-0 text-[#939294]" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="z-[130] w-[320px] border-black/10 bg-white p-0 dark:border-white/10 dark:bg-surface">
          <Command shouldFilter>
            <CommandInput
              placeholder={`Search ${channel.label}`}
              value={customValue}
              onValueChange={onCustomChange}
            />
            <CommandList>
              <CommandEmpty>No built subchannels found.</CommandEmpty>
              <CommandGroup>
                {channel.subchannels.map((subchannel) => (
                  <CommandItem
                    key={subchannel.key}
                    value={subchannel.label}
                    onSelect={() => onSelect(subchannel)}
                    className="cursor-pointer data-[selected=true]:bg-[#F3F8F5] data-[selected=true]:text-text-strong"
                  >
                    {subchannel.label}
                  </CommandItem>
                ))}
                {trimmedCustom && !channel.subchannels.some((subchannel) => subchannel.label.toLowerCase() === trimmedCustom.toLowerCase()) && (
                  <CommandItem
                    value={trimmedCustom}
                    onSelect={() => onSelect({ key: null, label: trimmedCustom })}
                    className="cursor-pointer data-[selected=true]:bg-[#F3F8F5] data-[selected=true]:text-text-strong"
                  >
                    Custom subchannel: {trimmedCustom}
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected?.label && selected.key === null && (
        <div className="mt-2 inline-flex max-w-full rounded-full border border-black/10 bg-white px-2.5 py-1 text-[12px] font-semibold text-text-strong dark:border-white/10 dark:bg-surface">
          <span className="truncate">Custom subchannel: {selected.label}</span>
        </div>
      )}
    </div>
  )
}
